/**這個Adapter已經使用了autoThrowErrorFetch，不需要在使用時另外加上autoThrowError了 */
import { useContext, useEffect } from "react";
import { API_Version, ApiVersionInfo, ResultObj, getControllerApiVersion, autoThrowErrorFetch } from "../Domain";
import { ApiContext, useApi } from "../apiProvider";
import { searchSettings } from "../types";
import { defaultDateToDotNet6Format, defaultTimeToDotNet6Format, dotNet6DateTimeToDefaulFormat } from "../function";

// useUserClockRecordPlaceApi 的 Hooks 使用方式
export const useUserClockRecordPlaceApi = () => {
    const apiManagerName = "useUserClockRecordPlaceApiManager";
    const context = useContext(ApiContext);
    if (!context) {
        throw new Error("useUserClockRecordPlaceApi must be used within a ApiProvider");
    }

    let { apiManagers, registerApiManager } = context;
    useEffect(() => {
        if (!apiManagers[apiManagerName]) {
            const newUserClockRecordPlaceApiManager = new UserClockRecordPlaceApiManager();
            newUserClockRecordPlaceApiManager.init().then((apiManager) => {
                registerApiManager(apiManagerName, apiManager);
            }).catch((e) => {
                console.error("Error initializing API Manager", e);
            });
        }
    }, [apiManagers, registerApiManager, apiManagerName]);

    return useApi<UserClockRecordPlaceApiManager>(apiManagerName);
};

// UserClockRecordPlaceApiInterface 的定義
export interface UserClockRecordPlaceApiInterface {
    /** 打卡地點查詢 */
    WorkClockPlaceSearch: (obj: WorkClockPlaceSearchParams) => Promise<ResultObj<WorkClockPlaceSearchResItem[]>>;
    /** 打卡地點新增 */
    WorkClockPlaceCreate: (obj: WorkClockPlaceCreateParams) => Promise<ResultObj>;
    /** 打卡地點修改 */
    WorkClockPlaceUpdate: (obj: WorkClockPlaceUpdateParams) => Promise<ResultObj>;
    /** 打卡地點IP限制更新 */
    WorkClockPlaceUpdateIP: (obj: WorkClockPlaceUpdateIPParams) => Promise<ResultObj>;
    /** 打卡地點刪除 */
    WorkClockPlaceDelete: (obj: WorkClockPlaceDeleteParams) => Promise<ResultObj>;
    /** 使用者打卡 */
    WorkClock: (obj: WorkClockParams) => Promise<ResultObj>;
    /** 打卡紀錄查詢 */
    WorkClockRecordSearch: (obj: WorkClockRecordSearchParams) => Promise<ResultObj<WorkClockRecordSearchResItem[]>>;
    /** 使用者補打卡 */
    WorkMakeUpClock: (obj: WorkMakeUpClockParams) => Promise<ResultObj>;
}

// UserClockRecordPlaceApiManager 類別實現
export class UserClockRecordPlaceApiManager implements UserClockRecordPlaceApiInterface {

    private adapter: UserClockRecordPlaceDotnet6Adapter | UserClockRecordPlaceFrameworkAdapter | null = null;
    private version: keyof typeof API_Version | undefined;

    constructor(version?: keyof typeof API_Version) {
        this.version = version;
    }

    async init(): Promise<UserClockRecordPlaceApiManager> {
        let target_version: string | undefined = this.version;
        if (target_version === undefined) {
            let getAPIversionResult: ApiVersionInfo | null = await getControllerApiVersion("UserClockRecordPlace");
            target_version = getAPIversionResult === null ? this.version : getAPIversionResult.version;
        }

        if (target_version === "dotNet6") this.adapter = new UserClockRecordPlaceDotnet6Adapter();
        else if (target_version === "dotNet6_AM_N") this.adapter = new UserClockRecordPlaceDotnet6Adapter(); // 預設是 dotNet6，有需要才再另寫Adapter
        else if (target_version === "framework") this.adapter = new UserClockRecordPlaceFrameworkAdapter();

        if (this.adapter !== null) {
            this.WorkClockPlaceSearch = this.adapter.WorkClockPlaceSearch;
            this.WorkClockPlaceCreate = this.adapter.WorkClockPlaceCreate;
            this.WorkClockPlaceUpdate = this.adapter.WorkClockPlaceUpdate;
            this.WorkClockPlaceUpdateIP = this.adapter.WorkClockPlaceUpdateIP;
            this.WorkClockPlaceDelete = this.adapter.WorkClockPlaceDelete;
            this.WorkClock = this.adapter.WorkClock;
            this.WorkClockRecordSearch = this.adapter.WorkClockRecordSearch;
            this.WorkMakeUpClock = this.adapter.WorkMakeUpClock;
        }
        return this;
    }

    emptyAdapter = async (): Promise<ResultObj<any>> => ({ success: false, message: 'API未實現', data: null });

    WorkClockPlaceSearch = async (obj: WorkClockPlaceSearchParams): Promise<ResultObj<WorkClockPlaceSearchResItem[]>> => await this.emptyAdapter();
    WorkClockPlaceCreate = async (obj: WorkClockPlaceCreateParams): Promise<ResultObj<number>> => await this.emptyAdapter();
    WorkClockPlaceUpdate = async (obj: WorkClockPlaceUpdateParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkClockPlaceUpdateIP = async (obj: WorkClockPlaceUpdateIPParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkClockPlaceDelete = async (obj: WorkClockPlaceDeleteParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkClock = async (obj: WorkClockParams): Promise<ResultObj> => await this.emptyAdapter();
    WorkClockRecordSearch = async (obj: WorkClockRecordSearchParams): Promise<ResultObj<WorkClockRecordSearchResItem[]>> => await this.emptyAdapter();
    WorkMakeUpClock = async (obj: WorkMakeUpClockParams): Promise<ResultObj> => await this.emptyAdapter();
}

class UserClockRecordPlaceBaseAdapter implements UserClockRecordPlaceApiInterface {
    WorkClockPlaceSearch = async (obj: WorkClockPlaceSearchParams): Promise<ResultObj<WorkClockPlaceSearchResItem[]>> => await autoThrowErrorFetch('UserClockRecordPlace/WorkClockPlaceSearch', obj);
    WorkClockPlaceCreate = async (obj: WorkClockPlaceCreateParams): Promise<ResultObj<number>> => await autoThrowErrorFetch('UserClockRecordPlace/WorkClockPlaceCreate', obj);
    WorkClockPlaceUpdate = async (obj: WorkClockPlaceUpdateParams): Promise<ResultObj> => await autoThrowErrorFetch('UserClockRecordPlace/WorkClockPlaceUpdate', obj);
    WorkClockPlaceUpdateIP = async (obj: WorkClockPlaceUpdateIPParams): Promise<ResultObj> => await autoThrowErrorFetch('UserClockRecordPlace/WorkClockPlaceUpdateIP', obj);
    WorkClockPlaceDelete = async (obj: WorkClockPlaceDeleteParams): Promise<ResultObj> => await autoThrowErrorFetch('UserClockRecordPlace/WorkClockPlaceDelete', obj);
    WorkClock = async (obj: WorkClockParams): Promise<ResultObj> => await autoThrowErrorFetch('UserClockRecordPlace/WorkClock', obj);
    WorkClockRecordSearch = async (obj: WorkClockRecordSearchParams): Promise<ResultObj<WorkClockRecordSearchResItem[]>> => await autoThrowErrorFetch('UserClockRecordPlace/WorkClockRecordSearch', obj);
    WorkMakeUpClock = async (obj: WorkMakeUpClockParams): Promise<ResultObj> => await autoThrowErrorFetch('UserClockRecordPlace/WorkMakeUpClock', obj);
}

class UserClockRecordPlaceDotnet6Adapter extends UserClockRecordPlaceBaseAdapter {
    WorkClock = async (obj: WorkClockParams): Promise<ResultObj> => await autoThrowErrorFetch('UserClockRecordPlace/WorkClock', {
        ...obj,
        date: defaultDateToDotNet6Format(obj.date),
        time: defaultTimeToDotNet6Format(obj.time),
    });
    WorkClockRecordSearch = async (obj: WorkClockRecordSearchParams): Promise<ResultObj<WorkClockRecordSearchResItem[]>> => await autoThrowErrorFetch('UserClockRecordPlace/WorkClockRecordSearch', {
        ...obj,
        date_start: defaultDateToDotNet6Format(obj.date_start),
        date_end: defaultDateToDotNet6Format(obj.date_end),
    }).then(res => ({
        ...res,
        data: (res.data || []).map((item: WorkClockRecordSearchResItem) => ({
            ...item,
            date: dotNet6DateTimeToDefaulFormat(item.date),
            time: dotNet6DateTimeToDefaulFormat(item.time),
        }))
    }));
    WorkMakeUpClock = async (obj: WorkMakeUpClockParams): Promise<ResultObj> => await autoThrowErrorFetch('UserClockRecordPlace/WorkMakeUpClock', {
        ...obj,
        date: defaultDateToDotNet6Format(obj.date),
        time: defaultTimeToDotNet6Format(obj.time),
    });
}

class UserClockRecordPlaceFrameworkAdapter extends UserClockRecordPlaceBaseAdapter {
    WorkClockPlaceSearch = async (obj: WorkClockPlaceSearchParams): Promise<ResultObj<WorkClockPlaceSearchResItem[]>> => await autoThrowErrorFetch('UserWork/WorkClockPlaceSearch', obj);
    WorkClockPlaceCreate = async (obj: WorkClockPlaceCreateParams): Promise<ResultObj<number>> => {
        const { visible, ...rest } = obj;
        return await autoThrowErrorFetch('UserWork/WorkClockPlaceCreate', rest);
    };
    WorkClockPlaceUpdate = async (obj: WorkClockPlaceUpdateParams): Promise<ResultObj> => await autoThrowErrorFetch('UserWork/WorkClockPlaceUpdate', obj);
    WorkClockPlaceUpdateIP = async (obj: WorkClockPlaceUpdateIPParams): Promise<ResultObj> => await autoThrowErrorFetch('UserWork/WorkClockPlaceUpdateIP', obj);
    WorkClockPlaceDelete = async (obj: WorkClockPlaceDeleteParams): Promise<ResultObj> => await autoThrowErrorFetch('UserWork/WorkClockPlaceDelete', obj);
    WorkClock = async (obj: WorkClockParams): Promise<ResultObj> => await autoThrowErrorFetch('UserWork/WorkClock', {
        user_id: obj.user_id,
        longitude: obj.longitude,
        latitude: obj.latitude,
    });
    WorkClockRecordSearch = async (obj: WorkClockRecordSearchParams): Promise<ResultObj<WorkClockRecordSearchResItem[]>> => await autoThrowErrorFetch('UserWork/WorkClockRecordSearch', obj);
    WorkMakeUpClock = async (obj: WorkMakeUpClockParams): Promise<ResultObj> => {
        const { is_makeup, ...rest } = obj;
        return await autoThrowErrorFetch('UserWork/WorkMakeUpClock', rest);
    };
}

/**
 * @description 打卡地點查詢（參數）
 * @param {number} place_id 流水號
 * @param {string} name 地點名稱
 * @param {string} visible 是否可視
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface WorkClockPlaceSearchParams {
    place_id?: number;
    name?: string;
    visible?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 打卡地點查詢（回傳）
 * @param {number} place_id 流水號
 * @param {string} name 地點名稱
 * @param {string} ips IP們限制
 * @param {number} distance 距離
 * @param {number} longitude 經度
 * @param {number} latitude 緯度
 * @param {string} visible 是否可視
 */
export interface WorkClockPlaceSearchResItem {
    place_id?: number;
    name?: string;
    ips?: string;
    distance?: number;
    longitude?: number;
    latitude?: number;
    visible?: string;
}

/**
 * @description 打卡地點新增（參數）
 * @param {string} name 地點名稱
 * @param {string[]} ips IP們限制
 * @param {number} distance 距離
 * @param {number} longitude 經度
 * @param {number} latitude 緯度
 * @param {string} visible 是否可視（僅用於dotnet6）
 */
export interface WorkClockPlaceCreateParams {
    name: string;
    ips: string[];
    distance: number;
    longitude: number;
    latitude: number;
    visible?: string; // 僅用於dotnet6
}

/**
 * @description 打卡地點修改（參數）
 * @param {number} place_id 流水號
 * @param {string} name 地點名稱
 * @param {number} distance 距離
 * @param {number} longitude 經度
 * @param {number} latitude 緯度
 * @param {string} visible 是否可視
 */
export interface WorkClockPlaceUpdateParams {
    place_id: number;
    name?: string;
    distance?: number;
    longitude?: number;
    latitude?: number;
    visible?: string;
}

/**
 * @description 打卡地點IP限制更新（參數）
 * @param {number} place_id 流水號
 * @param {string[]} ips IP們限制
 */
export interface WorkClockPlaceUpdateIPParams {
    place_id: number;
    ips: string[];
}

/**
 * @description 打卡地點刪除（參數）
 * @param {number} place_id 流水號
 */
export interface WorkClockPlaceDeleteParams {
    place_id: number;
}

/**
 * @description 使用者打卡（參數）
 * @param {string} user_id 帳號編號（不給預設拿token）
 * @param {string} date 日期
 * @param {string} time 時間
 * @param {number} longitude 經度
 * @param {number} latitude 緯度
 * @param {boolean} is_makeup 是否補打卡
 * @param {string} type 種類（上班、下班）
 * @param {string} makeup_reason 補打卡原因
 * @param {string} note 備註
 */
export interface WorkClockParams {
    user_id?: string;
    date?: string;
    time?: string;
    longitude?: number;
    latitude?: number;
    is_makeup?: boolean;
    type?: string;
    makeup_reason?: string;
    note?: string;
}

/**
 * @description 打卡紀錄查詢（參數）
 * @param {number} place_id 打卡地點流水號
 * @param {string} type 種類（上班、下班）
 * @param {string} date_start 日期起
 * @param {string} date_end 日期迄
 * @param {string} isvalid 是否有效
 * @param {string} is_makeup 是否補打卡
 * @param {string[]} user_ids 帳號編號們
 * @param {string} company_id 公司流水號
 * @param {string} general_manager_id 高階管理階層（前總經理室）流水號
 * @param {string} department_id 部門流水號
 * @param {string} position_id 據點流水號
 * @param {string} class_id 課級流水號
 * @param {string} group_id 組級流水號
 * @param {string} office_id 辦公室流水號
 * @param {string} excel 是否匯出
 * @param {number} page 頁碼
 * @param {number} num_per_page 一頁幾筆
 */
export interface WorkClockRecordSearchParams {
    place_id?: number;
    type?: string;
    date_start?: string;
    date_end?: string;
    isvalid?: string;
    is_makeup?: string;
    [searchSettings.user_ids]?: string[];
    [searchSettings.company_id]?: string;
    [searchSettings.general_manager_id]?: string;
    [searchSettings.department_id]?: string;
    [searchSettings.position_id]?: string;
    [searchSettings.class_id]?: string;
    [searchSettings.group_id]?: string;
    [searchSettings.office_id]?: string;
    excel?: string;
    page?: number;
    num_per_page?: number;
}

/**
 * @description 打卡紀錄查詢（回傳）
 * @param {string} cre_name 建立者姓名
 * @param {string} cre_time 建立時間
 * @param {string} date 日期
 * @param {number} distance 距離
 * @param {number} longitude 經度
 * @param {number} latitude 緯度
 * @param {string} invalid_reason 無效原因
 * @param {string} ips IP們限制
 * @param {string} is_makeup 是否補打卡
 * @param {string} isvalid 是否有效
 * @param {string} makeup_reason_name 補打卡原因名稱
 * @param {string} name 地點名稱
 * @param {string} note 備註
 * @param {string} place_id 打卡地點流水號
 * @param {string} place_name 打卡地點名稱
 * @param {string} sc_code 班表代碼
 * @param {string} status 狀態
 * @param {string} time 時間
 * @param {string} type 種類（上班、下班）
 * @param {string} user_id 帳號編號
 * @param {string} username 帳號
 * @param {string} visible 是否可視
 */
export interface WorkClockRecordSearchResItem {
    cre_name: string;
    cre_time: string;
    date: string;
    distance: number;
    longitude: number;
    latitude: number;
    invalid_reason: string;
    ips: string;
    is_makeup: string;
    isvalid: string;
    makeup_reason_name: string;
    name: string;
    note: string;
    place_id: string;
    place_name: string;
    sc_code: string;
    status: string;
    time: string;
    type: string;
    user_id: string;
    username: string;
    visible: string;
}

/**
 * @description 使用者補打卡（參數）
 * @param {string} user_id 帳號編號
 * @param {string} date 日期
 * @param {string} time 時間
 * @param {number} longitude 經度
 * @param {number} latitude 緯度
 * @param {string} makeup_reason 補打卡原因
 * @param {string} type 種類（上班、下班）
 * @param {string} note 備註
 * @param {boolean} is_makeup 是否補打卡（僅用於dotnet6）
 */
export interface WorkMakeUpClockParams {
    user_id: string;
    date: string;
    time: string;
    longitude?: number;
    latitude?: number;
    makeup_reason: string;
    type: string;
    note?: string;
    is_makeup?: boolean; // 僅用於dotnet6
}
